import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProjectProvider } from "@/context/ProjectContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Piano from "@/pages/Piano";
import AreaTest from "@/pages/AreaTest";
import MilestoneCliente from "@/pages/MilestoneCliente";
import SprintReview from "@/pages/SprintReview";
import Documenti from "@/pages/Documenti";
import Progetti from "@/pages/Progetti";
import Organizzazioni from "@/pages/Organizzazioni";
import Utenti from "@/pages/Utenti";
import Storico from "@/pages/Storico";
import Qualita from "@/pages/Qualita";
import "@/App.css";

const Loading = () => (
  <div className="grid min-h-screen place-items-center bg-[#F4F6F9]">
    <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
  </div>
);

const Gate = ({ children, roles }) => {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const LoginGate = () => {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Login />;
};

function App() {
  return (
    <AuthProvider>
      <ProjectProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginGate />} />
            <Route element={<Gate><Layout /></Gate>}>
              <Route path="/dashboard" element={<Dashboard />} />

              <Route
                path="/piano-interno"
                element={
                  <Gate roles={["admin"]}>
                    <Piano
                      scope="all"
                      titolo="Piano Interno"
                      sottotitolo="Vista completa di configurazione: sposti i task fra Stefano Critelli e Majorbit, modifichi entrambe le percentuali, riassegni, elimini e importi il piano da Excel."
                    />
                  </Gate>
                }
              />

              <Route
                path="/area-dev"
                element={
                  <Gate roles={["admin", "dev"]}>
                    <Piano
                      scope="cliente"
                      titolo="Area Dev"
                      sottotitolo="Operi su tutti i task e consolidi le percentuali dichiarate dal fornitore. Puoi portare un task fino a 'In Test': da quel momento l'esito è del tester."
                    />
                  </Gate>
                }
              />

              <Route
                path="/area-pm"
                element={
                  <Gate roles={["admin", "pm"]}>
                    <Piano
                      scope="cliente"
                      readOnly
                      titolo="Piano di progetto"
                      sottotitolo="Vista di sola lettura su tutti i task, le percentuali dichiarate e quelle consolidate."
                    />
                  </Gate>
                }
              />

              <Route path="/area-test" element={<Gate roles={["admin", "dev", "tester"]}><AreaTest /></Gate>} />

              <Route
                path="/area-fornitore"
                element={
                  <Gate roles={["admin", "fornitore"]}>
                    <Piano
                      scope="fornitore"
                      titolo="I miei task"
                      sottotitolo="Solo i task in carico a Stefano Critelli: aggiorni la tua percentuale, lo stato fino a 'In Test' e le note."
                    />
                  </Gate>
                }
              />

              <Route
                path="/milestone-cliente"
                element={<Gate roles={["admin", "pm", "dev", "cliente_finale"]}><MilestoneCliente /></Gate>}
              />
              <Route path="/sprint-review" element={<SprintReview />} />
              <Route path="/documenti" element={<Documenti />} />
              <Route path="/storico" element={<Storico />} />
              <Route
                path="/qualita"
                element={
                  <Gate roles={["admin", "pm", "dev", "tester", "cliente_finale"]}>
                    <Qualita />
                  </Gate>
                }
              />
              <Route path="/organizzazioni" element={<Gate roles={["admin"]}><Organizzazioni /></Gate>} />
              <Route path="/progetti" element={<Gate roles={["admin"]}><Progetti /></Gate>} />
              <Route path="/utenti" element={<Gate roles={["admin"]}><Utenti /></Gate>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </ProjectProvider>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
